import { formatCurrency, formatNumber, formatPercentage } from '../../../CommonUtils/formatNumber';

export type ColumnKey =
  | 'name'
  | 'ticker'
  | 'units'
  | 'price'
  | 'marketValue'
  | 'allocation'
  | 'annualIncomeRate'
  | 'estimatedAnnualIncome'
  | 'currentYield'
  | 'yieldOnCost';

export interface SortBy {
  column: ColumnKey;
  sort: 'none' | 'asc' | 'desc';
}

export type CellType = 'number' | 'string' | 'currency' | 'percentage';

interface Column {
  key: ColumnKey;
  header: string;
  align: 'left' | 'right';
  type: CellType;
}

export const COLUMNS: Column[] = [
  { key: 'name', align: 'left', type: 'string', header: 'Name' },
  { key: 'ticker', align: 'left', type: 'string', header: 'Ticker' },
  { key: 'units', align: 'right', type: 'number', header: 'Units' },
  { key: 'price', align: 'right', type: 'currency', header: 'Price' },
  { key: 'marketValue', align: 'right', type: 'currency', header: 'Market Value' },
  { key: 'allocation', align: 'right', type: 'percentage', header: 'Allocation' },
  { key: 'annualIncomeRate', align: 'right', type: 'currency', header: 'Annual Income Rate' },
  {
    key: 'estimatedAnnualIncome',
    align: 'right',
    type: 'currency',
    header: 'Estimated Annual Income',
  },
  { key: 'currentYield', align: 'right', type: 'percentage', header: 'Current Yield' },
  { key: 'yieldOnCost', align: 'right', type: 'percentage', header: 'Yield on Cost' },
];

export interface Cell {
  text: string | null;
  align: 'left' | 'right';
}

export type Row = {
  id?: string;
  color?: string | null;
  cells: Cell[];
  childRows?: Row[];
};

export interface TableData {
  columns: Column[];
  rows: Row[];
}

export interface IncomeDetailsData {
  tabList: string[];
  tableData: TableData | null;
  tabIndex: number;
}

export const formatCellValue = (value: number | string | null, type: CellType) => {
  if (value === null) {
    return '';
  }

  if (type === 'string') {
    return `${value}`;
  }

  if (typeof value !== 'number') {
    throw new Error('Value must be a number');
  }

  if (type === 'currency') {
    return formatCurrency(value, { mantissa: 2 });
  }

  if (type === 'percentage') {
    return formatPercentage(value, { mantissa: 2 });
  }

  return formatNumber(value, {
    mantissa: 2,
    optionalMantissa: true,
    trimMantissa: true,
  });
};
